import React from 'react';
import { FloatingArrow, FloatingArrowProps } from '@floating-ui/react';
import { ColorsType } from '@vds/core';

/**
 * Popover Arrow Props
 *
 * The props of the PopoverArrow component - https://floating-ui.com/docs/arrow
 * @param context - The context of the arrow
 * @param viewBox - The viewBox of the arrow
 * @param width - The width of the arrow
 * @param fill - The color fill of the arrow (CSS variable name). When undefined, uses --tooltip-arrow (set from tooltip bg per variant in Tooltip) with fallback --color-fg-primary.
 * @param ref - The reference of the arrow
 * @default
 * - context: 'top'
 * - viewBox: '0 0 12 12'
 * - width: 12
 * - fill: when undefined, uses var(--tooltip-arrow, var(--color-fg-primary)); Tooltip sets --tooltip-arrow from tooltip bg per variant.
 */
export interface PopoverArrowProps extends React.SVGProps<SVGSVGElement> {
  /** The reference of the arrow */
  ref?: React.Ref<SVGSVGElement>;
  /** The svg context of the arrow */
  context?: FloatingArrowProps['context'];
  /** The svg viewBox of the arrow */
  viewBox?: string;
  /** The svg width of the arrow */
  width?: number;
  /** The svg fill color of the arrow (CSS variable name). When undefined, uses --tooltip-arrow (tooltip bg per variant) or --color-fg-primary. */
  fill?: ColorsType;
}

/**
 * Popover Arrow
 *
 * The arrow of the popover component
 * @param ref - The reference of the arrow
 * @param context - The context of the arrow
 * @param viewBox - The viewBox of the arrow
 * @param width - The width of the arrow
 * @param fill - The color fill of the arrow
 * @returns The PopoverArrow component
 */
export const PopoverArrow = React.forwardRef<SVGSVGElement, PopoverArrowProps>(
  ({ context, viewBox = '0 0 12 12', width = 12, fill }, ref) => {
    const fillValue =
      fill !== undefined ? `var(${fill})` : 'var(--tooltip-arrow, var(--color-fg-primary))';
    return (
      <FloatingArrow
        ref={ref}
        className="vds-popover-arrow"
        context={context as FloatingArrowProps['context']}
        viewBox={viewBox}
        width={width}
        fill={fillValue}
      />
    );
  },
);
